import React, { useCallback, useMemo } from 'react';
import {
  Circle, Group, Path, Text,
} from 'react-konva';
import {
  MarkerProps, MarkerTooltipProps, MarkerWithHeadingProps, CircleMarkerProps,
} from 'types/webPlayer';
import { UNITS_LABELS } from '../utils/units';

const UNITS_MAPPING: Record<string, string> = {
  s: 'speed',
  hr: 'hr',
};

const FONT_FAMILY = 'Roboto';
const DEFAULT_TEXT_COLOR = '#828282';

// Costanti per il tooltip
const TOOLTIP_CURVE = 4;
const TOOLTIP_LINE_HEIGHT = 14;
const TOOLTIP_PADDING = 8;
const TOOLTIP_TEXT_OFFSET_X = 5;
const TOOLTIP_TEXT_OFFSET_Y = 8;

// Path del tooltip
const createTooltipPath = (width: number, height: number): string => {
  const curve = TOOLTIP_CURVE;
  const halfWidth = width / 2;

  return `M 0 ${curve}
    L 0 ${height - 1}
    Q 0 ${height} 1 ${height} 
    L ${width - 1} ${height}
    Q ${width} ${height} ${width} ${height - 1} 
    L ${width} ${curve} 
    Q ${width} 3 ${width - 1} 3 
    L ${halfWidth - 2} 3 
    L ${halfWidth} 0 
    L ${halfWidth + 2} 3 
    L 1 3 
    Q 0 3 0 ${curve}`;
};

const MarkerTooltip: React.FC<MarkerTooltipProps> = React.memo(({
  x, y, width, textLines,
}: MarkerTooltipProps) => {
  // Pre-calcola le dimensioni del tooltip
  const tooltipMetrics = useMemo(() => {
    const height = textLines.length * TOOLTIP_LINE_HEIGHT + TOOLTIP_PADDING;
    const pathData = createTooltipPath(width, height);

    return { height, pathData };
  }, [textLines.length, width]);

  const pathProps = useMemo(() => ({
    fill: 'white',
    stroke: '#555',
    strokeWidth: 1,
    opacity: 0.8,
    shadowBlur: 5,
    shadowColor: '#000',
    shadowOpacity: 0.5,
    shadowOffsetY: 3,
  }), []);

  const textProps = useMemo(() => ({
    fill: '#222',
    fontSize: 10,
    fontFamily: FONT_FAMILY,
  }), []);

  return (
    <Group x={x} y={y} className="marker-tooltip">
      <Path
        {...pathProps}
        data={tooltipMetrics.pathData}
      />
      {textLines.map((text, idx) => (
        <Text
          key={text}
          x={TOOLTIP_TEXT_OFFSET_X}
          y={TOOLTIP_LINE_HEIGHT * idx + TOOLTIP_TEXT_OFFSET_Y}
          text={text}
          {...textProps}
        />
      ))}
    </Group>
  );
});

// eslint-disable-next-line max-len
const MARKER_PATH_DATA = 'm0.92685 3.9174c-0.70024-0.7531-0.58809-2.094 0.06826-2.7972 0.55827-0.58557 1.7278-0.95894 2.6115-0.38378l5.7718 3.587c0.93416-1.0369 2.4882-1.5346 3.6931-1.5403 1.2849-0.0061 2.7343 0.49998 3.6661 1.5084l5.7767-3.611c0.99561-0.5021 1.9744-0.12226 2.5327 0.46323 0.65576 0.6879 0.68306 2.0988 0.01193 2.7709l-7.5029 7.4333c-0.9115 1.438-2.6867 2.3877-4.4846 2.3877-1.6233-6e-6 -3.2496-0.80646-4.1862-2.0156z';

const MarkerWithHeading: React.FC<MarkerWithHeadingProps> = React.memo(({
  color,
  posX,
  posY,
  active,
  hover,
  handlers,
  position,
  bearing,
}: MarkerWithHeadingProps) => (
  <Group
    rotation={(position.h !== undefined && position.h !== null) ? position.h - bearing : 0}
    x={posX}
    y={posY}
  >
    <Path
      data={MARKER_PATH_DATA}
      strokeWidth={active || hover ? 3 : 1.849}
      stroke={active ? 'white' : hover ? 'orange' : 'white'}
      fill={color}
      offsetX={13}
      offsetY={10}
      {...handlers}
    />
  </Group>
));

const CircleMarker: React.FC<CircleMarkerProps> = React.memo(({
  color, active, hover, handlers, posX, posY,
}: CircleMarkerProps) => {
  return (
    <Circle
      fill={color}
      radius={active || hover ? 8 : 5}
      stroke={active ? 'transparent' : hover ? 'orange' : 'white'}
      x={posX}
      y={posY}
      {...handlers}
    />
  );
});

const Marker: React.FC<MarkerProps> = React.memo((props: MarkerProps) => {
  const {
    active,
    bearing,
    color,
    hover,
    id,
    onClick,
    onDblClick,
    onMouseEnter,
    onMouseLeave,
    playerPoint,
    scale,
    showExtraData,
    speedMultiplier,
    text,
    textColor,
    userUnits,
  } = props;

  if (!playerPoint) {
    return null;
  }

  // Memorizza gli handlers
  const handlers = useMemo(() => ({
    onClick: () => onClick?.(id),
    onDblClick: onDblClick ? () => onDblClick(id) : undefined,
    onMouseEnter: onMouseEnter ? () => onMouseEnter(id) : undefined,
    onMouseLeave: onMouseLeave || undefined,
  }), [onClick, onDblClick, onMouseEnter, onMouseLeave, id]);

  const posX = playerPoint && playerPoint.x !== undefined && playerPoint.x !== null
    ? playerPoint.x * scale
    : 0; // (position?.x ?? 0) * scale
  const posY = playerPoint && playerPoint.y !== undefined && playerPoint.y !== null
    ? playerPoint.y * scale
    : 0; // (position?.y ?? 0) * scale

  const getUnitLabel = useCallback((key: string): string => {
    const mappedKey = UNITS_MAPPING[key];
    return userUnits?.[mappedKey]
      ? UNITS_LABELS[userUnits[mappedKey]]
      : key === 's' ? 'km/h' : 'bpm';
  }, [userUnits]);

  const tooltipTextData = useMemo(() => {
    const data = [
      (playerPoint?.s !== undefined && playerPoint?.s !== null)
      && `${(playerPoint.s * speedMultiplier).toFixed(2)} ${getUnitLabel('s')}`,
      (playerPoint?.hr !== undefined && playerPoint?.hr !== null)
      && `${playerPoint.hr.toFixed(0)} ${getUnitLabel('hr')}`,
    ].filter(Boolean);
    return data as string[];
  }, [playerPoint, getUnitLabel]);

  const textWidth = text ? text.length * 4 : 0;

  // if (!tooltipTextData.length) return null;

  if (!(
    typeof posX === 'number' && typeof posY === 'number'
    && !Number.isNaN(posX) && !Number.isNaN(posY)
  )) {
    return null;
  }

  return (
    <>
      {
        (playerPoint.h !== undefined && playerPoint.h !== null)
        ? (
          <MarkerWithHeading
            position={playerPoint}
            color={color}
            posX={posX}
            posY={posY}
            bearing={bearing || 0}
            active={active}
            hover={hover}
            handlers={handlers}
          />
        )
        : (
          <CircleMarker
            color={color}
            active={active}
            hover={hover}
            handlers={handlers}
            posX={posX}
            posY={posY}
          />
        )
      }

      {text && (
        <Text
          align="center"
          text={text}
          x={posX - textWidth}
          y={posY - 17}
          fill={textColor || DEFAULT_TEXT_COLOR}
          fontSize={12}
          fontFamily={FONT_FAMILY}
        />
      )}

      {showExtraData && tooltipTextData && (
        <MarkerTooltip
          x={posX - 30}
          y={posY + 10}
          width={60}
          textLines={tooltipTextData}
        />
      )}
    </>
  );
});

export default Marker;
